import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useCallback, useMemo, useState } from "react";
import { useScheduleReactivationMutation } from "src/api/services/subscriptionsApi";
import { PatientSubscriptions } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton/LoadingButton";
import { desktopSx } from "src/utils/styles/modalStyles";
import { FormControl } from "@mui/material";
import { PickerValue } from "@mui/x-date-pickers/internals";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { isError } from "src/utils/isError";
import { startOfTomorrow } from "date-fns";
import DatePicker from "src/components/DatePicker";

interface Props {
  subscription: PatientSubscriptions;
  onClose: () => void;
  open: boolean;
}

const ScheduleReactivationModal = ({ subscription, onClose, open }: Props) => {
  const [error, setError] = useState("");
  const [date, setDate] = useState<Date | null>(null);
  const [scheduleReactivation, { isLoading }] =
    useScheduleReactivationMutation();
  const errorLog = useSentryLogger("error");

  const tomorrow = useMemo(() => startOfTomorrow(), []);

  const handleBirthday = useCallback((value: PickerValue) => {
    setDate(value);
  }, []);

  const handleScheduleReactivation = useCallback(async () => {
    if (date == null) {
      setError("Please select a date");
      return;
    }

    setError("");

    try {
      await scheduleReactivation({
        id: subscription.id,
        body: { scheduled_for: date.toISOString() },
        patient_id: subscription.patient_id,
      }).unwrap();

      onClose();
    } catch (err: unknown) {
      if (isError(err)) {
        errorLog(err.data.message);
        setError(err.data.message);
      } else {
        errorLog(`Unable to schedule reactivation`);
        setError("Unable to schedule reactivation");
      }
    }
  }, [
    date,
    errorLog,
    onClose,
    scheduleReactivation,
    subscription.id,
    subscription.patient_id,
  ]);

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={2}>
        <Typography fontWeight={700} variant="h5">
          Schedule Reactivation
        </Typography>
        <FormControl>
          <DatePicker
            value={date}
            onChange={handleBirthday}
            minDate={tomorrow}
          />
        </FormControl>
        <Stack gap={1}>
          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error}
            </Typography>
          ) : null}

          <LoadingButton
            loading={isLoading}
            onClick={handleScheduleReactivation}
          >
            Schedule
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default ScheduleReactivationModal;
